import { Projectile } from '../entities/Projectile';
import { Player } from '../entities/Player';
import { Projectile as WeaponProjectile } from './WeaponSystem';

export class ProjectileSystem {
  private projectiles: Projectile[] = [];
  private weaponProjectiles: WeaponProjectile[] = [];
  private readonly MAX_PROJECTILES = 100;

  public addPlayerBullet(x: number, y: number, player: Player): void {
    if (this.projectiles.length >= this.MAX_PROJECTILES) {
      // Remove oldest inactive projectile
      const inactiveIndex = this.projectiles.findIndex(p => !p.active);
      if (inactiveIndex !== -1) {
        this.projectiles.splice(inactiveIndex, 1);
      }
    }

    const piercing = player.hasPiercingShot();
    const wideBeam = player.hasWideShot();

    if (wideBeam) {
      // Fire 3 bullets in a spread
      this.projectiles.push(new Projectile(x, y, 8, true, piercing));
      this.projectiles.push(new Projectile(x - 10, y, 8, true, piercing));
      this.projectiles.push(new Projectile(x + 10, y, 8, true, piercing));
    } else {
      // Fire single bullet
      this.projectiles.push(new Projectile(x, y, 8, true, piercing));
    }
  }

  public addWeaponProjectile(projectile: WeaponProjectile): void {
    if (this.weaponProjectiles.length < this.MAX_PROJECTILES) {
      this.weaponProjectiles.push(projectile);
    }
  }

  public addEnemyBullet(x: number, y: number, speed: number = 4): void {
    if (this.projectiles.length >= this.MAX_PROJECTILES) return;
    
    this.projectiles.push(new Projectile(x, y, speed, false));
  }

  public addBossBullet(x: number, y: number): void {
    if (this.projectiles.length >= this.MAX_PROJECTILES) return;
    
    const bullet = new Projectile(x, y, 1, false);
    // Custom velocity for boss bullets
    // We'll just use the basic implementation for now
    this.projectiles.push(bullet);
  }

  public update(deltaTime: number): void {
    for (const projectile of this.projectiles) {
      if (projectile.active) {
        projectile.update(deltaTime);
      }
    }

    // Update weapon projectiles
    for (const proj of this.weaponProjectiles) {
      if (proj.active) {
        proj.x += proj.vx;
        proj.y += proj.vy;
        
        // Update age for FM Super Blast
        if (proj.age !== undefined && proj.maxAge !== undefined) {
          proj.age += deltaTime * 1000;
          if (proj.age >= proj.maxAge) {
            proj.active = false;
          }
        }
        
        // Deactivate if off-screen
        if (proj.y < -20 || proj.y > 960 || proj.x < -20 || proj.x > 720) {
          proj.active = false;
        }
      }
    }

    // Remove inactive projectiles
    this.projectiles = this.projectiles.filter(p => p.active);
    this.weaponProjectiles = this.weaponProjectiles.filter(p => p.active);
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Render weapon projectiles
    for (const proj of this.weaponProjectiles) {
      if (proj.active) {
        ctx.save();
        
        // Different rendering based on weapon type
        switch (proj.weaponType) {
          case 1: // Wave Cannon - expanding rings
            this.renderWaveCannon(ctx, proj);
            break;
          case 2: // Wave Signal - thin laser
            this.renderWaveSignal(ctx, proj);
            break;
          case 6: // FM Super Blast - pulsing orb
            this.renderSuperBlast(ctx, proj);
            break;
          default:
            // Standard projectile
            ctx.fillStyle = proj.color;
            ctx.shadowColor = proj.color;
            ctx.shadowBlur = 10;
            ctx.fillRect(proj.x - proj.width / 2, proj.y - proj.height / 2, proj.width, proj.height);
        }
        
        ctx.restore();
      }
    }
    
    // Render standard projectiles
    for (const projectile of this.projectiles) {
      if (projectile.active) {
        projectile.render(ctx);
      }
    }
  }

  private renderWaveCannon(ctx: CanvasRenderingContext2D, proj: WeaponProjectile): void {
    // Love Wave: Simple heart-shaped energy wave
    const time = Date.now() * 0.008;
    const age = proj.age || 0;
    const maxAge = proj.maxAge || 2000;
    const lifeProgress = Math.min(age / maxAge, 1);
    
    const baseSize = 20 + lifeProgress * 20; // Smaller, simpler
    
    ctx.save();
    
    // Simple heart shape with glow
    ctx.fillStyle = proj.color;
    ctx.shadowColor = proj.color;
    ctx.shadowBlur = 15;
    ctx.globalAlpha = 0.8;
    
    this.drawHeart(ctx, proj.x, proj.y, baseSize);
    
    // Simple central glow
    ctx.globalAlpha = 0.9;
    ctx.shadowBlur = 20;
    ctx.fillStyle = '#ffffff';
    this.drawHeart(ctx, proj.x, proj.y, baseSize * 0.3);
    
    ctx.restore();
  }
  
  private drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
    ctx.beginPath();
    const topCurveHeight = size * 0.3;
    
    ctx.moveTo(x, y + topCurveHeight);
    // Top left curve
    ctx.bezierCurveTo(
      x - size / 2, y - size / 4,
      x - size, y + size / 4,
      x - size / 2, y + size
    );
    // Bottom left curve
    ctx.bezierCurveTo(
      x - size / 2, y + size * 1.25,
      x, y + size * 1.25,
      x, y + size * 1.25
    );
    // Bottom right curve
    ctx.bezierCurveTo(
      x, y + size * 1.25,
      x + size / 2, y + size * 1.25,
      x + size / 2, y + size
    );
    // Top right curve
    ctx.bezierCurveTo(
      x + size, y + size / 4,
      x + size / 2, y - size / 4,
      x, y + topCurveHeight
    );
    
    ctx.fill();
  }

  private renderWaveSignal(ctx: CanvasRenderingContext2D, proj: WeaponProjectile): void {
    // Light Laser: Simple piercing beam
    const age = proj.age || 0;
    const maxAge = proj.maxAge || 2000;
    const lifeProgress = Math.min(age / maxAge, 1);
    
    const beamLength = 80 + lifeProgress * 40; // Shorter beam
    const beamWidth = 8 + lifeProgress * 6;
    
    ctx.save();
    
    // Simple beam with glow
    ctx.strokeStyle = proj.color;
    ctx.lineWidth = beamWidth;
    ctx.lineCap = 'round';
    ctx.shadowColor = proj.color;
    ctx.shadowBlur = 20;
    ctx.globalAlpha = 0.8;
    
    ctx.beginPath();
    ctx.moveTo(proj.x, proj.y);
    ctx.lineTo(proj.x, proj.y - beamLength);
    ctx.stroke();
    
    // Simple tip glow
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 25;
    
    ctx.beginPath();
    ctx.arc(proj.x, proj.y - beamLength, beamWidth * 0.4, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }

  private renderRainbowRay(ctx: CanvasRenderingContext2D, proj: WeaponProjectile): void {
    // Rainbow Ray: Simple expanding rainbow square
    const time = Date.now() * 0.01;
    const age = proj.age || 0;
    const maxAge = proj.maxAge || 2000;
    const lifeProgress = Math.min(age / maxAge, 1);
    
    const baseSize = 12 + lifeProgress * 24; // Grow from 12 to 36 pixels
    
    ctx.save();
    
    // Simple rainbow square
    const rainbowColors = ['#ff0080', '#ff8000', '#ffff00'];
    for (let layer = 0; layer < 3; layer++) {
      const layerSize = baseSize + layer * 4;
      const alpha = Math.max(0.2, 1 - layer * 0.3);
      const colorIndex = (layer + Math.floor(time)) % rainbowColors.length;
      
      ctx.fillStyle = rainbowColors[colorIndex];
      ctx.globalAlpha = alpha;
      ctx.shadowColor = rainbowColors[colorIndex];
      ctx.shadowBlur = 20 + layer * 8;
      
      ctx.fillRect(
        proj.x - layerSize / 2,
        proj.y - layerSize / 2,
        layerSize,
        layerSize
      );
    }
    
    // Simple central glow
    ctx.globalAlpha = 0.9;
    ctx.shadowBlur = 30;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(
      proj.x - baseSize * 0.2,
      proj.y - baseSize * 0.2,
      baseSize * 0.4,
      baseSize * 0.4
    );
    
    ctx.restore();
  }

  private renderHopeBurst(ctx: CanvasRenderingContext2D, proj: WeaponProjectile): void {
    // Hope Burst: Simple expanding rings with central glow
    const time = Date.now() * 0.012;
    const age = proj.age || 0;
    const maxAge = proj.maxAge || 2000;
    const lifeProgress = Math.min(age / maxAge, 1);
    
    const baseRadius = 20 + lifeProgress * 40; // Grow from 20 to 60 pixels
    
    ctx.save();
    
    // Simple expanding rings
    const ringColors = ['#ffff00', '#ffaa00', '#ff6600'];
    for (let ring = 0; ring < 4; ring++) {
      const ringRadius = baseRadius + ring * 10;
      const alpha = Math.max(0.2, 1 - ring * 0.2);
      const colorIndex = (ring + Math.floor(time * 0.5)) % ringColors.length;
      
      ctx.strokeStyle = ringColors[colorIndex];
      ctx.lineWidth = 8 - ring * 1.5;
      ctx.shadowColor = ringColors[colorIndex];
      ctx.shadowBlur = 25 + ring * 8;
      ctx.globalAlpha = alpha;
      
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Simple central core
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 0.9;
    ctx.shadowBlur = 35;
    
    const coreSize = baseRadius * 0.3 + Math.sin(time * 4) * 4;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y, coreSize, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }

  private renderUnitySpread(ctx: CanvasRenderingContext2D, proj: WeaponProjectile): void {
    // Unity Spread: Simple protective arcs with central core
    const time = Date.now() * 0.01;
    const age = proj.age || 0;
    const maxAge = proj.maxAge || 2000;
    const lifeProgress = Math.min(age / maxAge, 1);
    
    const arcLength = 40 + lifeProgress * 30; // Grow from 40 to 70 pixels
    const spreadAngle = Math.PI * 1.2; // Wide spread
    const startAngle = -spreadAngle / 2;
    
    ctx.save();
    ctx.lineCap = 'round';
    
    // Simple protective arcs
    const arcColors = ['#ff4500', '#ff6600', '#ff8800'];
    for (let i = 0; i < 6; i++) {
      const angle = startAngle + spreadAngle * (i / 5);
      const colorIndex = i % arcColors.length;
      
      ctx.strokeStyle = arcColors[colorIndex];
      ctx.lineWidth = 10;
      ctx.shadowColor = arcColors[colorIndex];
      ctx.shadowBlur = 25;
      ctx.globalAlpha = 0.8;
      
      ctx.beginPath();
      ctx.moveTo(proj.x, proj.y);
      const endX = proj.x + Math.cos(angle) * arcLength;
      const endY = proj.y - Math.sin(angle) * arcLength;
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }
    
    // Simple central rings
    for (let ring = 0; ring < 3; ring++) {
      const ringRadius = 20 + ring * 6;
      ctx.strokeStyle = arcColors[ring % arcColors.length];
      ctx.lineWidth = 6 - ring;
      ctx.shadowBlur = 20 + ring * 5;
      ctx.globalAlpha = 0.7;
      
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Central core
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 0.9;
    ctx.shadowBlur = 30;
    
    const coreSize = 12 + lifeProgress * 8;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y, coreSize, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }

  private renderSuperBlast(ctx: CanvasRenderingContext2D, proj: WeaponProjectile): void {
    // Harmony Blast: Simple pulsing orb with expanding rings
    const time = Date.now() * 0.008;
    const age = proj.age || 0;
    const maxAge = proj.maxAge || 2000;
    const lifeProgress = Math.min(age / maxAge, 1);
    
    const baseRadius = 25 + lifeProgress * 35; // Grow from 25 to 60 pixels
    
    ctx.save();
    
    // Simple expanding rings
    const ringColors = ['#8a2be2', '#9932cc', '#ba55d3'];
    for (let ring = 0; ring < 3; ring++) {
      const ringRadius = baseRadius + ring * 8;
      const alpha = Math.max(0.2, 1 - ring * 0.3);
      
      ctx.strokeStyle = ringColors[ring % ringColors.length];
      ctx.lineWidth = 6 - ring * 1.5;
      ctx.shadowColor = ringColors[ring % ringColors.length];
      ctx.shadowBlur = 25 + ring * 10;
      ctx.globalAlpha = alpha;
      
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Simple central core with glow
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 0.9;
    ctx.shadowBlur = 40;
    
    const coreSize = baseRadius * 0.4 + Math.sin(time * 3) * 5;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y, coreSize, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }

  public getProjectiles(): Projectile[] {
    return this.projectiles;
  }

  public clear(): void {
    this.projectiles = [];
    this.weaponProjectiles = [];
  }

  public clearEnemyBullets(): void {
    this.projectiles = this.projectiles.filter(p => p.isFriendly());
  }
  
  public getWeaponProjectiles(): WeaponProjectile[] {
    return this.weaponProjectiles;
  }
}
